/**
 * ISO 3166-1 alpha-2 country catalog for SafeGeo country rules.
 * Names via Intl.DisplayNames (en); codes are the persistence format.
 */

/** Common / all assignable region codes (excludes XK, EU aggregates, etc.). */
const ISO_CODES = [
  "AD","AE","AF","AG","AI","AL","AM","AO","AQ","AR","AS","AT","AU","AW","AX","AZ",
  "BA","BB","BD","BE","BF","BG","BH","BI","BJ","BL","BM","BN","BO","BQ","BR","BS","BT","BV","BW","BY","BZ",
  "CA","CC","CD","CF","CG","CH","CI","CK","CL","CM","CN","CO","CR","CU","CV","CW","CX","CY","CZ",
  "DE","DJ","DK","DM","DO","DZ",
  "EC","EE","EG","EH","ER","ES","ET",
  "FI","FJ","FK","FM","FO","FR",
  "GA","GB","GD","GE","GF","GG","GH","GI","GL","GM","GN","GP","GQ","GR","GS","GT","GU","GW","GY",
  "HK","HM","HN","HR","HT","HU",
  "ID","IE","IL","IM","IN","IO","IQ","IR","IS","IT",
  "JE","JM","JO","JP",
  "KE","KG","KH","KI","KM","KN","KP","KR","KW","KY","KZ",
  "LA","LB","LC","LI","LK","LR","LS","LT","LU","LV","LY",
  "MA","MC","MD","ME","MF","MG","MH","MK","ML","MM","MN","MO","MP","MQ","MR","MS","MT","MU","MV","MW","MX","MY","MZ",
  "NA","NC","NE","NF","NG","NI","NL","NO","NP","NR","NU","NZ",
  "OM",
  "PA","PE","PF","PG","PH","PK","PL","PM","PN","PR","PS","PT","PW","PY",
  "QA",
  "RE","RO","RS","RU","RW",
  "SA","SB","SC","SD","SE","SG","SH","SI","SJ","SK","SL","SM","SN","SO","SR","SS","ST","SV","SX","SY","SZ",
  "TC","TD","TF","TG","TH","TJ","TK","TL","TM","TN","TO","TR","TT","TV","TW","TZ",
  "UA","UG","UM","US","UY","UZ",
  "VA","VC","VE","VG","VI","VN","VU",
  "WF","WS",
  "YE","YT",
  "ZA","ZM","ZW",
] as const;

export type CountryOption = { code: string; name: string };

let cached: CountryOption[] | null = null;

export function allCountries(): CountryOption[] {
  if (cached) return cached;
  let dn: Intl.DisplayNames | null = null;
  try {
    dn = new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    dn = null;
  }
  cached = ISO_CODES.map((code) => ({
    code,
    name: (dn?.of(code) || code).toString(),
  })).sort((a, b) => a.name.localeCompare(b.name, "en"));
  return cached;
}

export function countryName(code: string): string {
  const c = code.toUpperCase();
  const hit = allCountries().find((x) => x.code === c);
  if (hit) return hit.name;
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(c) || c;
  } catch {
    return c;
  }
}

export function filterCountries(
  query: string,
  exclude: string[] = [],
): CountryOption[] {
  const q = query.trim().toLowerCase();
  const skip = new Set(exclude.map((c) => c.toUpperCase()));
  return allCountries().filter((c) => {
    if (skip.has(c.code)) return false;
    if (!q) return true;
    return (
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  });
}

const ISO_SET = new Set<string>(ISO_CODES);

/**
 * Horizon / Markets-style “popular” shortcuts (shown above the full list).
 * @see https://github.com/Shopify/horizon/blob/main/snippets/localization-form.liquid
 */
export const POPULAR_COUNTRY_CODES = [
  "US",
  "CA",
  "GB",
  "AU",
  "DE",
  "FR",
  "IE",
  "NZ",
  "NL",
  "JP",
] as const;

/** EU/EEA ISO codes - blocking needs merchant `legal` confirmation (compiler EU_BLOCK). */
export const EEA_COUNTRY_CODES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE","IS","LI","NO",
]);

export function isEeaCountry(code: string): boolean {
  return EEA_COUNTRY_CODES.has(code.toUpperCase());
}

/** EU-27 only (v6 Add rule groups) - excludes EEA extras IS/LI/NO. */
export const EU27_CODES = [
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
] as const;

/** v6 §27.1 country group chips on Add country rule. */
export const COUNTRY_GROUPS: Array<{ id: string; label: string; codes: string[] }> = [
  { id: "eu", label: "EU (27)", codes: [...EU27_CODES] },
  { id: "nordics", label: "Nordics", codes: ["DK", "FI", "IS", "NO", "SE"] },
  {
    id: "na",
    label: "North America",
    codes: ["US", "CA", "MX"],
  },
  {
    id: "gulf",
    label: "Gulf",
    codes: ["AE", "BH", "KW", "OM", "QA", "SA"],
  },
];

export function popularCountries(exclude: string[] = []): CountryOption[] {
  const skip = new Set(exclude.map((c) => c.toUpperCase()));
  const all = allCountries();
  return POPULAR_COUNTRY_CODES.map((code) => all.find((c) => c.code === code))
    .filter((c): c is CountryOption => !!c && !skip.has(c.code));
}

/** Format like Horizon: “Canada” (code only in a11y / advanced). */
export function countryDisplayName(code: string): string {
  return countryName(code);
}

/** Parse bulk paste: "GB, IE" or newlines → ISO codes. */
export function parseBulkCountryCodes(raw: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(/[\s,;]+/)) {
    const c = part.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(c) || !ISO_SET.has(c) || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}
